<mat-expansion-panel
  data-testid="campaign-export-panel"
  class="cta-summary-expansion-panel cta-summary-expansion-panel-creator campaign-export-panel-accordion"
  [class.campaign-export-panel-accordion-settled]="expansionSettled()"
  [expanded]="expanded()"
  (opened)="onPanelOpened()"
  (closed)="onPanelClosed()"
  (afterExpand)="onAfterExpand()"
>
  <mat-expansion-panel-header class="!h-[72px] !min-h-[72px] !py-0 !pl-0 !pr-4">
    <mat-panel-title>
      <div class="flex items-center w-full min-w-0 h-[72px]">
        <div
          cdkDragHandle
          class="flex items-center justify-center h-full w-10 shrink-0"
          [class.cursor-grab]="!dragDisabled()"
          [class.cursor-not-allowed]="dragDisabled()"
          [class.opacity-30]="dragDisabled()"
          (click)="$event.stopPropagation()"
        >
          <app-icon src="/icons/grip-vertical.svg" alt="drag" [size]="20" />
        </div>

        <app-icon-badge
          content="icons/folder-share.svg"
          backgroundColor="var(--shikenso-pink-base)"
          color="var(--shikenso-purple-base)"
          [size]="48"
          [iconSize]="28"
          [borderRadius]="1000"
        />

        <div class="flex items-center justify-between gap-2 w-full min-w-0 ml-2">
          <div class="flex flex-col justify-center gap-1 min-w-0 flex-1">
            <div class="flex gap-2 items-center">
              <app-editable-title
                fontClasses="text-sm font-bold"
                [control]="nameControl"
                [icon]="null"
                [maxWidth]="280"
                [gateEditMode]="true"
                [disabled]="controlsDisabled()"
                (editModeRequested)="onNameEditIconClick()"
                (editModeChanged)="onNameEditModeChanged($event)"
              />

              @let nameError = nameErrorMessage();

              @if (nameError && nameEditModeActive()) {
                <span class="text-shikenso-error-form-field text-[10px]">
                  {{ nameError }}
                </span>
              }
            </div>

            <div class="flex items-center gap-2 flex-wrap">
              <div class="flex items-center gap-1 px-2 py-1 rounded-full bg-shikenso-gray-base text-xs">
                <app-icon src="/icons/calendar.svg" alt="date range" [size]="12" />
                {{ timeRangeBadge() }}
              </div>
              <div class="flex items-center gap-1 px-2 py-1 rounded-full bg-shikenso-gray-base text-xs">
                <app-icon [src]="insightLevelIcon()" alt="insight level" [size]="12" />
                {{ insightLevelBadge() }}
              </div>
              <div class="flex items-center gap-1 px-2 py-1 rounded-full bg-shikenso-gray-base text-xs">
                <app-icon src="/icons/copy.svg" alt="custom configs" [size]="12" />
                {{ customConfigsBadge() }}
              </div>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div
              class="flex items-center justify-center text-xs font-bold leading-[22px] px-4 py-[1px] min-w-24 rounded-full"
              matTooltipPosition="above"
              [ngClass]="{
                'text-shikenso-warning-base bg-shikenso-warning-light': campaignExport().isActive && isValueDirty(),
                'text-shikenso-success-base bg-shikenso-success-light': campaignExport().isActive && !isValueDirty(),
                'text-shikenso-error-base bg-shikenso-error-light': !campaignExport().isActive,
              }"
              [matTooltip]="getPublishStatusTooltip()"
            >
              @if (campaignExport().isActive) {
                {{ 'pages.campaignDetails.export.published' | translate }}
              } @else {
                {{ 'pages.campaignDetails.export.unpublished' | translate }}
              }
            </div>

            <button
              mat-icon-button
              type="button"
              class="!flex items-center justify-center"
              [matMenuTriggerFor]="exportActionsMenu"
              (click)="$event.stopPropagation()"
              #menuTrigger="matMenuTrigger"
            >
              <app-icon src="/icons/dots.svg" alt="more options" [size]="16" />
            </button>

            <mat-menu #exportActionsMenu="matMenu">
              <button
                mat-menu-item
                type="button"
                [disabled]="controlsDisabled() || !campaignExport().isActive"
                (click)="$event.stopPropagation(); menuTrigger.closeMenu(); onCopyLinkClick()"
              >
                {{ 'pages.campaignDetails.export.copyLink' | translate }}
              </button>

              <button
                mat-menu-item
                type="button"
                [disabled]="controlsDisabled()"
                (click)="$event.stopPropagation(); menuTrigger.closeMenu(); onCloneClick()"
              >
                {{ 'pages.campaignDetails.export.cloneExport' | translate }}
              </button>

              <button
                mat-menu-item
                type="button"
                [disabled]="controlsDisabled()"
                (click)="$event.stopPropagation(); menuTrigger.closeMenu(); onDeleteClick()"
              >
                {{ 'shared.delete' | translate }}
              </button>
            </mat-menu>
          </div>
        </div>
      </div>
    </mat-panel-title>
  </mat-expansion-panel-header>

  <form [formGroup]="form" class="custom-form" (ngSubmit)="onSaveClick()">
    <div class="flex flex-col gap-6 p-4 relative">
      <!-- Time Range -->
      <div class="flex flex-col gap-3 mb-3">
        <div>
          <h5 class="text-base font-bold">{{ 'pages.campaignDetails.export.timeRange' | translate }}</h5>
          <p class="text-xs text-shikenso-purple-base-60 mt-1">
            {{ 'pages.campaignDetails.export.timeRangeSubtitle' | translate }}
          </p>
        </div>

        <app-profile-date-filter
          [class.pointer-events-none]="controlsDisabled()"
          [campaignAnalytics]="campaignAnalytics()"
          [initialState]="exportDateFilterInitialState()"
          [allowedDateRange]="exportDateRangeBounds()"
          [hideRelativeRangeOptions]="true"
          (dateRangeChanged)="onExportDateRangeChanged($event)"
          (selectedRangeChanged)="onExportSelectedRangeChanged($event)"
        />
      </div>

      <!-- Insight Level -->
      <div class="flex flex-col gap-3">
        <div>
          <h5 class="text-base font-bold">{{ 'pages.campaignDetails.export.insightLevel' | translate }}</h5>
          <p class="text-xs text-shikenso-purple-base-60 mt-1">
            {{ 'pages.campaignDetails.export.insightLevelSubtitle' | translate }}
          </p>
        </div>

        <div class="flex flex-col gap-2">
          <!-- Overview Only -->
          <button
            type="button"
            class="flex items-center gap-3 pl-2 py-3 pr-4 border rounded-2xl cursor-pointer transition-colors w-full text-left bg-transparent"
            [class.border-shikenso-pink-base]="form.get('detailLevel')?.value === EDetailLevel.generalInfo"
            [class.border-shikenso-purple-base-12]="form.get('detailLevel')?.value !== EDetailLevel.generalInfo"
            [disabled]="controlsDisabled()"
            (click)="onDetailLevelSelect(EDetailLevel.generalInfo)"
          >
            <app-icon-badge
              content="icons/magnify.svg"
              backgroundColor="var(--shikenso-gray-base)"
              color="var(--shikenso-purple-base)"
              [size]="40"
              [iconSize]="16"
            />
            <div class="flex flex-col">
              <span class="font-bold leading-6">
                {{ 'pages.campaignDetails.export.overviewOnly' | translate }}
              </span>
              <span class="text-xs text-shikenso-purple-base-60">
                {{ 'pages.campaignDetails.export.overviewOnlyDescription' | translate }}
              </span>
            </div>
          </button>

          <!-- Analytics Summary -->
          <button
            type="button"
            class="flex items-center gap-3 pl-2 py-3 pr-4 border rounded-2xl cursor-pointer transition-colors w-full text-left bg-transparent"
            [class.border-shikenso-pink-base]="form.get('detailLevel')?.value === EDetailLevel.generalAnalytics"
            [class.border-shikenso-purple-base-12]="form.get('detailLevel')?.value !== EDetailLevel.generalAnalytics"
            [disabled]="controlsDisabled()"
            (click)="onDetailLevelSelect(EDetailLevel.generalAnalytics)"
          >
            <app-icon-badge
              content="icons/analytics.svg"
              backgroundColor="var(--shikenso-gray-base)"
              color="var(--shikenso-purple-base)"
              [size]="40"
              [iconSize]="16"
            />
            <div class="flex flex-col">
              <span class="font-bold leading-6">
                {{ 'pages.campaignDetails.export.analyticsSummary' | translate }}
              </span>
              <span class="text-xs text-shikenso-purple-base-60">
                {{ 'pages.campaignDetails.export.analyticsSummaryDescription' | translate }}
              </span>
            </div>
          </button>

          <!-- Full Analytics -->
          <button
            type="button"
            class="flex items-center gap-3 pl-2 py-3 pr-4 border rounded-2xl cursor-pointer transition-colors w-full text-left bg-transparent"
            [class.border-shikenso-pink-base]="form.get('detailLevel')?.value === EDetailLevel.detailedAnalytics"
            [class.border-shikenso-purple-base-12]="form.get('detailLevel')?.value !== EDetailLevel.detailedAnalytics"
            [disabled]="controlsDisabled()"
            (click)="onDetailLevelSelect(EDetailLevel.detailedAnalytics)"
          >
            <app-icon-badge
              content="icons/infinity.svg"
              backgroundColor="var(--shikenso-gray-base)"
              color="var(--shikenso-purple-base)"
              [size]="40"
              [iconSize]="16"
            />
            <div class="flex flex-col">
              <span class="font-bold leading-6">
                {{ 'pages.campaignDetails.export.fullAnalytics' | translate }}
              </span>
              <span class="text-xs text-shikenso-purple-base-60">
                {{ 'pages.campaignDetails.export.fullAnalyticsDescription' | translate }}
              </span>
            </div>
          </button>
        </div>
      </div>

      <!-- Visible Data -->
      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between w-full">
          <div>
            <h5 class="text-base font-bold">{{ 'pages.campaignDetails.export.visibleData' | translate }}</h5>
            <p class="text-xs text-shikenso-purple-base-60 mt-1">
              {{ 'pages.campaignDetails.export.visibleDataSubtitle' | translate }}
            </p>
          </div>

          <button
            mat-flat-button
            type="button"
            color="secondary-gray"
            class="mat-tiny-button"
            (click)="expandCollapseVisibleData()"
          >
            <span class="text-xs">
              {{ allVisibleDataExpanded() ? ('shared.collapseAll' | translate) : ('shared.expandAll' | translate) }}
            </span>
          </button>
        </div>

        <div class="flex flex-col gap-3">
          <!-- Tabs Panel -->
          <mat-expansion-panel
            class="cta-summary-expansion-panel cta-summary-expansion-panel-creator"
            [expanded]="expandedPanels().has('tabs')"
            (expandedChange)="onPanelToggle('tabs', $event)"
          >
            <mat-expansion-panel-header class="!px-3 !py-3" data-testid="campaign-export-panel-tabs-section-header">
              <mat-panel-title>
                <div class="flex flex-1 items-center justify-between">
                  <div class="flex flex-col justify-center">
                    <span class="text-base font-bold">
                      {{ 'pages.campaignDetails.export.tabs' | translate }}
                    </span>
                    <span class="text-xs text-shikenso-purple-base-60">
                      {{ 'pages.campaignDetails.export.tabsSubtitle' | translate }}
                    </span>
                  </div>

                  <div class="flex items-center gap-2 mr-2">
                    @if (!allTabsSelected() && selectedTabs.size > 0) {
                      <span class="text-sm text-shikenso-purple-base-60">
                        {{ selectedTabs.size }} / {{ EXPORT_TABS.length }}
                      </span>
                    }

                    @if (selectedTabs.size === 0) {
                      <div class="flex items-center gap-1">
                        <app-icon
                          src="/icons/warning.svg"
                          alt="Warning"
                          color="var(--shikenso-warning-base)"
                          [size]="16"
                        />
                        <span class="text-xs text-shikenso-warning-base">
                          {{ 'pages.campaignDetails.export.tabsWarning' | translate }}
                        </span>
                      </div>
                    }
                  </div>
                </div>
              </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="grid grid-cols-3 gap-x-14 gap-y-4 py-3 px-8">
              @for (tab of EXPORT_TABS; track tab) {
                <div class="flex gap-2 items-center justify-between">
                  <mat-label class="text-sm">
                    {{ TAB_TRANSLATION_KEYS[tab] | translate }}
                  </mat-label>

                  <mat-slide-toggle
                    [attr.data-testid]="'campaign-export-tab-toggle-' + tab"
                    [checked]="selectedTabs.has(tab)"
                    [hideIcon]="true"
                    [disabled]="controlsDisabled()"
                    (change)="onTabChange($event.checked, tab)"
                  />
                </div>
              }
            </div>
          </mat-expansion-panel>

          <!-- General Panel -->
          <mat-expansion-panel
            class="cta-summary-expansion-panel cta-summary-expansion-panel-creator"
            [expanded]="expandedPanels().has('general')"
            (expandedChange)="onPanelToggle('general', $event)"
          >
            <mat-expansion-panel-header class="!px-3 !py-3">
              <mat-panel-title>
                <div class="flex flex-1 items-center justify-between">
                  <div class="flex flex-col justify-center">
                    <span class="text-base font-bold">
                      {{ 'pages.campaignDetails.export.general' | translate }}
                    </span>
                    <span class="text-xs text-shikenso-purple-base-60">
                      {{ 'pages.campaignDetails.export.generalSubtitle' | translate }}
                    </span>
                  </div>

                  @if (selectedGeneralCount() < TOTAL_GENERAL_TOGGLES) {
                    <span class="text-sm text-shikenso-purple-base-60 mr-2">
                      {{ selectedGeneralCount() }} / {{ TOTAL_GENERAL_TOGGLES }}
                    </span>
                  }
                </div>
              </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="flex items-center gap-8 py-3 px-8">
              <!-- Budget -->
              <div class="flex gap-2 items-center">
                <mat-label class="text-sm">{{ 'pages.campaignDetails.export.budget' | translate }}</mat-label>
                <mat-slide-toggle
                  [checked]="!!generalFormValue()?.budget"
                  [hideIcon]="true"
                  [disabled]="controlsDisabled()"
                  (change)="onBudgetToggle($event.checked)"
                />
              </div>
            </div>
          </mat-expansion-panel>

          <!-- Content Types Panel -->
          <mat-expansion-panel
            class="cta-summary-expansion-panel cta-summary-expansion-panel-creator"
            [expanded]="expandedPanels().has('contentTypes')"
            (expandedChange)="onPanelToggle('contentTypes', $event)"
          >
            <mat-expansion-panel-header class="!px-3 !py-3">
              <mat-panel-title>
                <div class="flex flex-1 items-center justify-between">
                  <div class="flex flex-col justify-center">
                    <span class="text-base font-bold">
                      {{ 'pages.campaignDetails.export.contentTypes' | translate }}
                    </span>
                    <span class="text-xs text-shikenso-purple-base-60">
                      {{ 'pages.campaignDetails.export.contentTypesSubtitle' | translate }}
                    </span>
                  </div>

                  <div class="flex items-center gap-2 mr-2">
                    @if (
                      selectedContentMatchingTypes.size > 0 &&
                      selectedContentMatchingTypes.size < CONTENT_MATCHING_TYPES.length
                    ) {
                      <span class="text-sm text-shikenso-purple-base-60">
                        {{ selectedContentMatchingTypes.size }} / {{ CONTENT_MATCHING_TYPES.length }}
                      </span>
                    }

                    @if (selectedContentMatchingTypes.size === 0) {
                      <div class="flex items-center gap-1">
                        <app-icon
                          src="/icons/warning.svg"
                          alt="Warning"
                          color="var(--shikenso-warning-base)"
                          [size]="16"
                        />
                        <span class="text-xs text-shikenso-warning-base">No contents will be shown.</span>
                      </div>
                    }
                  </div>
                </div>
              </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="flex gap-2 flex-wrap py-3 px-4">
              @for (matchingType of CONTENT_MATCHING_TYPES; track matchingType) {
                <app-selectable-status-badge
                  [status]="matchingType"
                  colorScheme="matching-status-borderless"
                  [selected]="selectedContentMatchingTypes.has(matchingType)"
                  [disabled]="controlsDisabled()"
                  (selectionChanged)="onContentMatchingTypeChange($event, matchingType)"
                />
              }
            </div>
          </mat-expansion-panel>

          <!-- Status Panel -->
          <mat-expansion-panel
            class="cta-summary-expansion-panel cta-summary-expansion-panel-creator"
            [expanded]="expandedPanels().has('status')"
            (expandedChange)="onPanelToggle('status', $event)"
          >
            <mat-expansion-panel-header class="!px-3 !py-3">
              <mat-panel-title>
                <div class="flex flex-1 items-center justify-between">
                  <div class="flex flex-col justify-center">
                    <span class="text-base font-bold">
                      {{ 'pages.campaignDetails.export.status' | translate }}
                    </span>
                    <span class="text-xs text-shikenso-purple-base-60">
                      {{ 'pages.campaignDetails.export.statusSubtitle' | translate }}
                    </span>
                  </div>

                  @let totalStatusCount =
                    selectedDeliverableStatuses.size +
                    selectedObservationStatuses.size +
                    selectedContentStatuses.size +
                    selectedKpiStatuses.size;
                  @let allStatusCount =
                    DELIVERABLE_STATUSES.length +
                    OBSERVATION_STATUSES.length +
                    CONTENT_STATUSES.length +
                    KPI_STATUSES.length;

                  <div class="flex items-center gap-2 mr-2">
                    @if (
                      selectedContentMatchingTypes.size > 0 && totalStatusCount > 0 && totalStatusCount < allStatusCount
                    ) {
                      <span class="text-sm text-shikenso-purple-base-60">
                        {{ totalStatusCount }} / {{ allStatusCount }}
                      </span>
                    }

                    @if (selectedContentMatchingTypes.size === 0 || totalStatusCount === 0) {
                      <div class="flex items-center gap-1">
                        <app-icon
                          src="/icons/warning.svg"
                          alt="Warning"
                          color="var(--shikenso-warning-base)"
                          [size]="16"
                        />
                        <span class="text-xs text-shikenso-warning-base">No status will be shown.</span>
                      </div>
                    }
                  </div>
                </div>
              </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="flex flex-col gap-4 py-3 px-4">
              @let noContentType = selectedContentMatchingTypes.size === 0;
              @let matchedNotSelected = !selectedContentMatchingTypes.has(EMatchingType.matched);

              <!-- Deliverable Status -->
              <div class="flex flex-col gap-2">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-shikenso-purple-base">
                    {{ 'pages.campaignDetails.export.deliverableStatus' | translate }}
                  </span>
                  @if (noContentType) {
                    <div class="flex items-center gap-1">
                      <app-icon
                        src="/icons/warning.svg"
                        alt="Warning"
                        color="var(--shikenso-warning-base)"
                        [size]="14"
                      />
                      <span class="text-xs text-shikenso-warning-base">No content type selected.</span>
                    </div>
                  } @else if (selectedDeliverableStatuses.size === 0) {
                    <div class="flex items-center gap-1">
                      <app-icon
                        src="/icons/warning.svg"
                        alt="Warning"
                        color="var(--shikenso-warning-base)"
                        [size]="14"
                      />
                      <span class="text-xs text-shikenso-warning-base">No deliverables will be shown.</span>
                    </div>
                  }
                </div>
                <div class="flex gap-2 flex-wrap">
                  @for (status of DELIVERABLE_STATUSES; track status) {
                    <app-selectable-status-badge
                      [status]="status"
                      colorScheme="deliverable"
                      [selected]="selectedDeliverableStatuses.has(status)"
                      [disabled]="controlsDisabled() || noContentType"
                      (selectionChanged)="onDeliverableStatusChange($event, status)"
                    />
                  }
                </div>
              </div>

              <hr class="border-shikenso-purple-base-12" />

              <!-- Observation Status -->
              <div class="flex flex-col gap-2">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-shikenso-purple-base">
                    {{ 'pages.campaignDetails.export.observationStatus' | translate }}
                  </span>
                  @if (noContentType) {
                    <div class="flex items-center gap-1">
                      <app-icon
                        src="/icons/warning.svg"
                        alt="Warning"
                        color="var(--shikenso-warning-base)"
                        [size]="14"
                      />
                      <span class="text-xs text-shikenso-warning-base">No content type selected.</span>
                    </div>
                  } @else if (selectedObservationStatuses.size === 0) {
                    <div class="flex items-center gap-1">
                      <app-icon
                        src="/icons/warning.svg"
                        alt="Warning"
                        color="var(--shikenso-warning-base)"
                        [size]="14"
                      />
                      <span class="text-xs text-shikenso-warning-base">No observations will be shown.</span>
                    </div>
                  }
                </div>
                <div class="flex gap-2 flex-wrap">
                  @for (status of OBSERVATION_STATUSES; track status) {
                    <app-selectable-status-badge
                      [status]="status"
                      colorScheme="tracking"
                      [selected]="selectedObservationStatuses.has(status)"
                      [disabled]="controlsDisabled() || noContentType"
                      (selectionChanged)="onObservationStatusChange($event, status)"
                    />
                  }
                </div>
              </div>

              <hr class="border-shikenso-purple-base-12" />

              <!-- Content Status -->
              <div class="flex flex-col gap-2">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-shikenso-purple-base">
                    {{ 'pages.campaignDetails.export.contentStatus' | translate }}
                  </span>
                  @if (noContentType) {
                    <div class="flex items-center gap-1">
                      <app-icon
                        src="/icons/warning.svg"
                        alt="Warning"
                        color="var(--shikenso-warning-base)"
                        [size]="14"
                      />
                      <span class="text-xs text-shikenso-warning-base">No content type selected.</span>
                    </div>
                  } @else if (matchedNotSelected) {
                    <div class="flex items-center gap-1">
                      <app-icon
                        src="/icons/warning.svg"
                        alt="Warning"
                        color="var(--shikenso-warning-base)"
                        [size]="14"
                      />
                      <span class="text-xs text-shikenso-warning-base">"Matched" content type not selected.</span>
                    </div>
                  } @else if (selectedContentStatuses.size === 0) {
                    <div class="flex items-center gap-1">
                      <app-icon
                        src="/icons/warning.svg"
                        alt="Warning"
                        color="var(--shikenso-warning-base)"
                        [size]="14"
                      />
                      <span class="text-xs text-shikenso-warning-base">No content status will be shown.</span>
                    </div>
                  }
                </div>
                <div class="flex gap-2 flex-wrap">
                  @for (status of CONTENT_STATUSES; track status) {
                    <app-selectable-status-badge
                      [status]="status"
                      colorScheme="content-status"
                      [selected]="selectedContentStatuses.has(status)"
                      [disabled]="controlsDisabled() || noContentType || matchedNotSelected"
                      (selectionChanged)="onContentStatusChange($event, status)"
                    />
                  }
                </div>
              </div>

              <hr class="border-shikenso-purple-base-12" />

              <!-- KPI Goal Status -->
              <div class="flex flex-col gap-2">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-shikenso-purple-base">
                    {{ 'pages.campaignDetails.export.kpiGoalStatus' | translate }}
                  </span>
                  @if (noContentType) {
                    <div class="flex items-center gap-1">
                      <app-icon
                        src="/icons/warning.svg"
                        alt="Warning"
                        color="var(--shikenso-warning-base)"
                        [size]="14"
                      />
                      <span class="text-xs text-shikenso-warning-base">No content type selected.</span>
                    </div>
                  } @else if (matchedNotSelected) {
                    <div class="flex items-center gap-1">
                      <app-icon
                        src="/icons/warning.svg"
                        alt="Warning"
                        color="var(--shikenso-warning-base)"
                        [size]="14"
                      />
                      <span class="text-xs text-shikenso-warning-base">"Matched" content type not selected.</span>
                    </div>
                  } @else if (selectedKpiStatuses.size === 0) {
                    <div class="flex items-center gap-1">
                      <app-icon
                        src="/icons/warning.svg"
                        alt="Warning"
                        color="var(--shikenso-warning-base)"
                        [size]="14"
                      />
                      <span class="text-xs text-shikenso-warning-base">No KPI goals will be shown.</span>
                    </div>
                  }
                </div>
                <div class="flex gap-2 flex-wrap">
                  @for (status of KPI_STATUSES; track status) {
                    <app-selectable-status-badge
                      [status]="status"
                      colorScheme="kpiGoals"
                      [selected]="selectedKpiStatuses.has(status)"
                      [disabled]="controlsDisabled() || noContentType || matchedNotSelected"
                      (selectionChanged)="onKpiStatusChange($event, status)"
                    />
                  }
                </div>
              </div>
            </div>
          </mat-expansion-panel>

          <!-- Platforms Panel -->
          <mat-expansion-panel
            class="cta-summary-expansion-panel cta-summary-expansion-panel-creator"
            [expanded]="expandedPanels().has('platforms')"
            (expandedChange)="onPanelToggle('platforms', $event)"
          >
            <mat-expansion-panel-header class="!px-3 !py-3">
              <mat-panel-title>
                <div class="flex flex-1 items-center justify-between">
                  <div class="flex flex-col justify-center">
                    <span class="text-base font-bold">
                      {{ 'pages.campaignDetails.export.platforms' | translate }}
                    </span>
                    <span class="text-xs text-shikenso-purple-base-60">
                      {{ 'pages.campaignDetails.export.platformsSubtitle' | translate }}
                    </span>
                  </div>

                  <div class="flex items-center gap-2 mr-2">
                    @if (!allPlatformFeaturesSelected() && selectedPlatformFeatures.size > 0) {
                      <span class="text-sm text-shikenso-purple-base-60">
                        {{ selectedPlatformFeatures.size }} / {{ platformFeatures().length }}
                      </span>
                    }

                    @if (platformFeatures().length > 0 && selectedPlatformFeatures.size === 0) {
                      <div class="flex items-center gap-1">
                        <app-icon
                          src="/icons/warning.svg"
                          alt="Warning"
                          color="var(--shikenso-warning-base)"
                          [size]="16"
                        />
                        <span class="text-xs text-shikenso-warning-base">
                          {{ 'pages.campaignDetails.export.platformsWarning' | translate }}
                        </span>
                      </div>
                    }
                  </div>
                </div>
              </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="flex gap-2 flex-wrap p-3">
              @for (platformFeature of platformFeatures(); track platformFeature) {
                <app-pill-checkbox
                  labelClasses="font-medium"
                  [label]="'shared.platformFeatures.' + platformFeature | translate"
                  [platform]="platformFeature"
                  [checked]="selectedPlatformFeatures.has(platformFeature)"
                  [disabled]="controlsDisabled()"
                  (checkboxStateChanged)="onPlatformFeatureChange($event, platformFeature)"
                />
              }
            </div>
          </mat-expansion-panel>

          <!-- KPIs Panel -->
          <mat-expansion-panel
            class="cta-summary-expansion-panel cta-summary-expansion-panel-creator"
            [expanded]="expandedPanels().has('kpis')"
            (expandedChange)="onPanelToggle('kpis', $event)"
          >
            <mat-expansion-panel-header class="!px-3 !py-3">
              <mat-panel-title>
                <div class="flex flex-1 items-center justify-between">
                  <div class="flex flex-col justify-center">
                    <span class="text-base font-bold">
                      {{ 'pages.campaignDetails.export.kpis' | translate }}
                    </span>
                    <span class="text-xs text-shikenso-purple-base-60">
                      {{ 'pages.campaignDetails.export.kpisSubtitle' | translate }}
                    </span>
                  </div>

                  <div class="flex items-center gap-2 mr-2">
                    @if (!allKpisSelected() && selectedKpis.size > 0) {
                      <span class="text-sm text-shikenso-purple-base-60">
                        {{ selectedKpis.size }} / {{ kpis().length }}
                      </span>
                    }

                    @if (kpis().length > 0 && selectedKpis.size === 0) {
                      <div class="flex items-center gap-1">
                        <app-icon
                          src="/icons/warning.svg"
                          alt="Warning"
                          color="var(--shikenso-warning-base)"
                          [size]="16"
                        />
                        <span class="text-xs text-shikenso-warning-base">
                          {{ 'pages.campaignDetails.export.kpisWarning' | translate }}
                        </span>
                      </div>
                    }
                  </div>
                </div>
              </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="flex flex-col gap-3 p-3">
              <mat-form-field class="w-full">
                <app-icon src="/icons/search.svg" matPrefix alt="Search" class="w-4 h-4" />
                <input matInput [placeholder]="'shared.search' | translate" [formControl]="kpiSearchControl" />

                @if (kpiSearchControl.value) {
                  <button matSuffix mat-icon-button type="button" (click)="kpiSearchControl.reset()">
                    <app-icon src="/icons/x-mark.svg" alt="Clear" class="w-4 h-4" />
                  </button>
                }
              </mat-form-field>

              <div class="flex gap-2">
                <button
                  mat-flat-button
                  color="secondary-gray-border"
                  class="mat-micro-button"
                  type="button"
                  [disabled]="controlsDisabled()"
                  (click)="selectAllKpis()"
                >
                  <span class="text-xs">{{ 'shared.selectAll' | translate }}</span>
                </button>
                <button
                  mat-flat-button
                  color="secondary-gray-border"
                  class="mat-micro-button"
                  type="button"
                  [disabled]="controlsDisabled()"
                  (click)="unselectAllKpis()"
                >
                  <span class="text-xs">{{ 'shared.unselectAll' | translate }}</span>
                </button>
              </div>

              <div class="flex gap-2 flex-wrap">
                @for (kpi of filteredKpis(); track kpi) {
                  <app-pill-checkbox
                    containerClasses="!gap-4"
                    labelClasses="!text-sm"
                    [label]="'shared.expectedKpis.' + kpi | translate"
                    [checked]="selectedKpis.has(kpi)"
                    [disabled]="controlsDisabled()"
                    (checkboxStateChanged)="onKpiChange($event, kpi)"
                  />
                }
              </div>
            </div>
          </mat-expansion-panel>

          <!-- Creators Panel -->
          <mat-expansion-panel
            class="cta-summary-expansion-panel cta-summary-expansion-panel-creator"
            [expanded]="expandedPanels().has('creators')"
            (expandedChange)="onPanelToggle('creators', $event)"
          >
            <mat-expansion-panel-header class="!px-3 !py-3">
              <mat-panel-title>
                <div class="flex flex-1 items-center justify-between">
                  <div class="flex flex-col justify-center">
                    <span class="text-base font-bold">
                      {{ 'pages.campaignDetails.export.creators' | translate }}
                    </span>
                    <span class="text-xs text-shikenso-purple-base-60">
                      {{ 'pages.campaignDetails.export.creatorsSubtitle' | translate }}
                    </span>
                  </div>

                  <div class="flex items-center gap-2 mr-2">
                    @if (!allCreatorsSelected() && selectedCreatorIds.size > 0) {
                      <span class="text-sm text-shikenso-purple-base-60">
                        {{ selectedCreatorIds.size }} / {{ creators().length }}
                      </span>
                    }

                    @if (creators().length > 0 && selectedCreatorIds.size === 0) {
                      <div class="flex items-center gap-1">
                        <app-icon
                          src="/icons/warning.svg"
                          alt="Warning"
                          color="var(--shikenso-warning-base)"
                          [size]="16"
                        />
                        <span class="text-xs text-shikenso-warning-base">
                          {{ 'pages.campaignDetails.export.creatorsWarning' | translate }}
                        </span>
                      </div>
                    }
                  </div>
                </div>
              </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="flex flex-col gap-3 p-3">
              <mat-form-field class="w-full">
                <app-icon src="/icons/search.svg" matPrefix alt="Search" class="w-4 h-4" />
                <input matInput [placeholder]="'shared.search' | translate" [formControl]="creatorSearchControl" />

                @if (creatorSearchControl.value) {
                  <button matSuffix mat-icon-button (click)="creatorSearchControl.reset()">
                    <app-icon src="/icons/x-mark.svg" alt="Clear" class="w-4 h-4" />
                  </button>
                }
              </mat-form-field>

              <div class="flex gap-2">
                <button
                  mat-flat-button
                  color="secondary-gray-border"
                  class="mat-micro-button"
                  type="button"
                  [disabled]="controlsDisabled()"
                  (click)="selectAllCreators()"
                >
                  <span class="text-xs">{{ 'shared.selectAll' | translate }}</span>
                </button>
                <button
                  mat-flat-button
                  color="secondary-gray-border"
                  class="mat-micro-button"
                  type="button"
                  [disabled]="controlsDisabled()"
                  (click)="unselectAllCreators()"
                >
                  <span class="text-xs">{{ 'shared.unselectAll' | translate }}</span>
                </button>
              </div>

              <div class="flex gap-2 flex-wrap">
                @for (creator of filteredCreators(); track creator.id) {
                  <app-pill-checkbox
                    containerClasses="!py-1 !pl-[6px] !gap-4"
                    labelContainerClasses="!gap-1"
                    labelClasses="!text-sm"
                    [label]="creator.name"
                    [icon]="creator?.icon ?? undefined"
                    [checked]="selectedCreatorIds.has(creator.id)"
                    [disabled]="controlsDisabled()"
                    (checkboxStateChanged)="onCreatorChange($event, creator.id)"
                  />
                }
              </div>
            </div>
          </mat-expansion-panel>
        </div>
      </div>

      <div
        class="sticky flex gap-2 bottom-[-1px] left-0 right-0 -mb-4 pt-4 pb-4 z-10 bg-white border-t border-shikenso-purple-base-12"
      >
        <div class="flex justify-between items-center w-full">
          <button
            mat-flat-button
            color="secondary"
            type="button"
            data-testid="campaign-export-panel-copy-link-button"
            [disabled]="
              controlsDisabled() || !form.get('link')?.value || !campaignExport().isActive || linkCopiedTimeout()
            "
            (click)="onCopyLinkClick()"
          >
            <div class="flex items-center gap-2">
              <app-icon src="/icons/copy.svg" alt="copy" [size]="16" />

              @if (linkCopiedTimeout()) {
                {{ 'pages.campaignDetails.export.copiedLink' | translate }}
              } @else {
                {{ 'pages.campaignDetails.export.copyLink' | translate }}
              }
            </div>
          </button>

          <div class="flex gap-3">
            <button
              mat-flat-button
              color="secondary-transparent"
              type="button"
              [disabled]="controlsDisabled()"
              (click)="onCancelClick()"
            >
              {{ 'shared.cancel' | translate }}
            </button>

            @if (campaignExport().isActive) {
              <button
                mat-flat-button
                color="danger"
                type="button"
                data-testid="campaign-export-panel-unpublish-button"
                [disabled]="controlsDisabled()"
                (click)="onUnpublish()"
              >
                @if (saving() && unpublishClicked()) {
                  <div class="min-w-16 flex items-center justify-center">
                    <app-dots-spinner />
                  </div>
                } @else {
                  {{ 'pages.campaignDetails.export.unpublish' | translate }}
                }
              </button>
            }

            @if (!campaignExport().isActive) {
              <button
                mat-flat-button
                color="success"
                type="button"
                data-testid="campaign-export-panel-publish-button"
                [disabled]="controlsDisabled()"
                (click)="onPublish()"
              >
                @if (saving() && publishClicked()) {
                  <div class="min-w-16 flex items-center justify-center">
                    <app-dots-spinner />
                  </div>
                } @else {
                  {{ 'pages.campaignDetails.export.publish' | translate }}
                }
              </button>
            }

            <button mat-flat-button type="submit" [disabled]="controlsDisabled()">
              @if (saving() && saveClicked()) {
                <app-dots-spinner [absoluteCentered]="true" />
              } @else {
                {{ 'shared.form.save' | translate }}
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  </form>
</mat-expansion-panel>
